
import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import {
  tierHasCapability,
  tierTeamSeatsMax,
  tierTeamSeatsPerWorkspace,
} from "@/lib/tier";
import { toast } from "sonner";
import {
  ROLE_LABEL,
  STATUS_LABEL,
  formatExpiry,
  type MemberRole,
  type MemberStatus,
} from "./workspaceMembers/useWorkspaceMembers";

type MemberRow = {
  id: string;
  workspace_id: string;
  member_user_id: string;
  role: MemberRole;
  status: MemberStatus;
  invited_at: string;
};

type InviteRow = {
  id: string;
  workspace_id: string;
  invited_email: string;
  role: MemberRole;
  token: string;
  expires_at: string;
};

/**
 * Agency-only "team across all workspaces" overview. Lives on the Account
 * tab so a workspace owner can see every active member + pending invite
 * grouped by workspace, with revoke actions in one place. Per-workspace
 * role changes and transfer-ownership still happen inside each workspace's
 * own Team members card.
 */
export const WorkspaceMembersOverviewCard = () => {
  const { user } = useAuth();
  const { allOwnedWorkspaces, primaryTier } = useWorkspace();
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [invites, setInvites] = useState<InviteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const hasTeamCapability = tierHasCapability(primaryTier, "team_members");
  const accountSeatsMax = tierTeamSeatsMax(primaryTier);
  const perWorkspaceMax = tierTeamSeatsPerWorkspace(primaryTier);

  // Only show this card on Agency. Elite has only 3 seats — the per-workspace
  // card is enough; rendering a second card would be noise.
  const visible = hasTeamCapability && primaryTier === "agency";

  const ownedWorkspaces = useMemo(
    () => (allOwnedWorkspaces ?? []).filter((w) => w.owner_user_id === user?.id),
    [allOwnedWorkspaces, user?.id],
  );
  const ownedIds = useMemo(() => ownedWorkspaces.map((w) => w.id), [ownedWorkspaces]);

  const load = useCallback(async () => {
    if (!visible || ownedIds.length === 0) {
      setMembers([]);
      setInvites([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const [{ data: m, error: mErr }, { data: i, error: iErr }] = await Promise.all([
      supabase
        .from("workspace_members")
        .select("id, workspace_id, member_user_id, role, status, invited_at")
        .in("workspace_id", ownedIds)
        .neq("status", "revoked")
        .order("invited_at", { ascending: true }),
      supabase
        .from("workspace_member_invites")
        .select("id, workspace_id, invited_email, role, token, expires_at")
        .in("workspace_id", ownedIds)
        .eq("status", "pending")
        .order("created_at", { ascending: false }),
    ]);
    if (mErr) toast.error(mErr.message);
    if (iErr) toast.error(iErr.message);
    setMembers((m ?? []) as MemberRow[]);
    setInvites((i ?? []) as InviteRow[]);
    setLoading(false);
  }, [visible, ownedIds]);

  useEffect(() => { load(); }, [load]);

  const revokeMember = async (member: MemberRow) => {
    if (!confirm("Revoke this member's access?")) return;
    setBusyId(member.id);
    const { error } = await supabase
      .from("workspace_members")
      .update({ status: "revoked", revoked_at: new Date().toISOString() })
      .eq("id", member.id);
    setBusyId(null);
    if (error) return toast.error(error.message);
    toast.success("Access revoked");
    setMembers((prev) => prev.filter((m) => m.id !== member.id));
  };

  const revokeInvite = async (inv: InviteRow) => {
    if (!confirm("Revoke this pending invite?")) return;
    setBusyId(inv.id);
    const { error } = await supabase
      .from("workspace_member_invites")
      .update({ status: "revoked" })
      .eq("id", inv.id);
    setBusyId(null);
    if (error) return toast.error(error.message);
    toast.success("Invite revoked");
    setInvites((prev) => prev.filter((i) => i.id !== inv.id));
  };

  if (!visible) return null;

  const totalUsage = members.length + invites.length;
  const remaining =
    accountSeatsMax === null ? null : Math.max(0, accountSeatsMax - totalUsage);

  const byWorkspace = ownedWorkspaces.map((ws) => ({
    workspace: ws,
    members: members.filter((m) => m.workspace_id === ws.id),
    invites: invites.filter((i) => i.workspace_id === ws.id),
  }));

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" /> Team across all workspaces
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Every team member and pending invite across the workspaces you own.{" "}
          {accountSeatsMax === null ? (
            <span>Unlimited account seats.</span>
          ) : (
            <span>{totalUsage} of {accountSeatsMax} account seats used{remaining !== null && ` · ${remaining} remaining`}.</span>
          )}
          {perWorkspaceMax !== null && (
            <span> Per-workspace cap: {perWorkspaceMax}.</span>
          )}
        </p>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : byWorkspace.every((w) => w.members.length === 0 && w.invites.length === 0) ? (
          <p className="text-sm text-muted-foreground">
            No team members or pending invites yet. Add teammates from each workspace's Team members card.
          </p>
        ) : (
          <div className="space-y-4">
            {byWorkspace.map(({ workspace, members: wsMembers, invites: wsInvites }) => {
              if (wsMembers.length === 0 && wsInvites.length === 0) return null;
              const wsUsage = wsMembers.length + wsInvites.length;
              return (
                <div key={workspace.id} className="rounded-md border border-border">
                  <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border bg-muted/30">
                    <p className="text-sm font-medium truncate">
                      {workspace.workspace_label || workspace.display_name || "Workspace"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {wsUsage}{perWorkspaceMax !== null ? ` / ${perWorkspaceMax}` : ""}
                    </p>
                  </div>
                  <ul className="divide-y divide-border">
                    {wsMembers.map((m) => (
                      <li key={m.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">
                            <span className="font-mono text-xs text-muted-foreground">{m.member_user_id.slice(0, 8)}</span>
                          </p>
                          <p className="text-xs text-muted-foreground flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] py-0">{ROLE_LABEL[m.role]}</Badge>
                            <span>{STATUS_LABEL[m.status]}</span>
                          </p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => revokeMember(m)} disabled={busyId === m.id}>
                          Revoke
                        </Button>
                      </li>
                    ))}
                    {wsInvites.map((inv) => (
                      <li key={inv.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{inv.invited_email}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] py-0">{ROLE_LABEL[inv.role]}</Badge>
                            <span>Pending · {formatExpiry(inv.expires_at)}</span>
                          </p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => revokeInvite(inv)} disabled={busyId === inv.id}>
                          Revoke invite
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
